import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTableModule } from '@angular/material/table';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { CategoriaFinanceiraService } from '../../../../../shared/services/apis/financas/categoria-financeira.service';
import { CategoriaFinanceira } from '../../../../../shared/services/models/financas/categoria-financeira.model';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { UtilService } from '../../../../../shared/services/util.service';

@Component({
  selector: 'app-servico-tomado-pagamento',
  standalone: true,
  imports: [
    ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
    MatDatepickerModule, MatTableModule, NgxCurrencyDirective, SelectPesquisavelComponent, AbntPipe
  ],
  templateUrl: './servico-tomado-pagamento.component.html',
  styleUrl: './servico-tomado-pagamento.component.css',
})
export class ServicoTomadoPagamentoComponent implements OnInit {
  @Input({ required: true }) form!: FormGroup;
  @Input() dynamicCurrencyMask: any;

  public listaCategoria: CategoriaFinanceira[] = [];
  public displayedColumns: string[] = ['numero', 'vencimento', 'valor', 'remover'];

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private categoriaFinanceiraService: CategoriaFinanceiraService,
  ) { }

  ngOnInit(): void {
    this.categoriaFinanceiraService.getLookups().subscribe({
      next: (res) => {
        if (!res.isError) this.listaCategoria = res.categoriasPai || [];
      }
    });
  }

  public get parcelas(): FormArray {
    return this.form.get('parcelas') as FormArray;
  }

  public get somaParcelas(): number {
    return this.parcelas.controls.reduce((soma, c) => soma + (Number(c.get('valorParcela')?.value) || 0), 0);
  }

  public get valorLiquido(): number {
    return Number(this.form.get('valorLiquido')?.value) || 0;
  }

  public get divergenciaParcelas(): boolean {
    return this.parcelas.length > 0 && Math.abs(this.somaParcelas - this.valorLiquido) > 0.01;
  }

  private novaParcela(numero: number, dataVencimento: Date, valor: number): FormGroup {
    return this.formBuilder.group({
      numeroParcela: [numero],
      dataVencimento: [dataVencimento],
      valorParcela: [UtilService.arredondarAbnt(valor, 2)],
    });
  }

  public removerParcela(index: number): void {
    this.parcelas.removeAt(index);
  }

  public adicionarParcela(): void {
    const ultima = this.parcelas.length ? this.parcelas.at(this.parcelas.length - 1).get('dataVencimento')?.value : new Date();
    const proximaData = new Date(ultima);
    proximaData.setDate(proximaData.getDate() + 30);
    this.parcelas.push(this.novaParcela(this.parcelas.length + 1, proximaData, 0));
  }

  /**
   * Interpreta a expressão de Condição de Pagamento e retorna os dias de vencimento de cada parcela.
   * Mesma sintaxe do editor de parcelas de Cotação (Processo de Compras): "30 60" (dias soltos,
   * separados por espaço/vírgula/;//), "3x" (N parcelas mensais) e "15 +2x" (1ª parcela em N dias
   * + M parcelas mensais a partir dela). Réplica deliberada — ver plano da feature para unificação futura.
   */
  private parseParcelasDias(expr: string): number[] {
    const s = (expr || '').trim();
    if (!s) return [];
    const x = s.match(/^(\d+)\s*x$/i);
    if (x) {
      const n = Math.max(1, Math.min(36, parseInt(x[1], 10)));
      return Array.from({ length: n }, (_, i) => 30 * (i + 1));
    }
    const plus = s.match(/^(\d+)\s*\+\s*(\d+)\s*x$/i);
    if (plus) {
      const first = parseInt(plus[1], 10);
      const extra = parseInt(plus[2], 10);
      const dias = [first];
      for (let i = 1; i <= extra; i++) dias.push(first + 30 * i);
      return dias;
    }
    return s.split(/[\s,;/]+/).map(t => parseInt(t, 10)).filter(n => Number.isFinite(n) && n >= 0);
  }

  public gerarParcelas(): void {
    const condicao = (this.form.get('condicaoPagamento')?.value || '').toString().trim().toLowerCase();
    const valorLiquido = this.valorLiquido;

    if (!valorLiquido) {
      this.alertService.show('Atenção', 'Informe o Valor do Serviço antes de gerar as parcelas.', AlertType.warning);
      return;
    }

    const diasVencimento = (!condicao || condicao === 'à vista' || condicao === 'a vista' || condicao === '0')
      ? [0]
      : this.parseParcelasDias(condicao);

    if (!diasVencimento.length) {
      this.alertService.show('Atenção', 'Não foi possível interpretar a condição de pagamento informada.', AlertType.warning);
      return;
    }

    while (this.parcelas.length) this.parcelas.removeAt(0);

    const valorParcela = UtilService.arredondarAbnt(valorLiquido / diasVencimento.length, 2);
    let somaParcial = 0;
    const hoje = new Date();

    diasVencimento.forEach((dias, index) => {
      const dataVencimento = new Date(hoje);
      dataVencimento.setDate(dataVencimento.getDate() + dias);
      const isUltima = index === diasVencimento.length - 1;
      // A última absorve a diferença de arredondamento, para a soma fechar no líquido.
      const valor = isUltima ? UtilService.arredondarAbnt(valorLiquido - somaParcial, 2) : valorParcela;
      somaParcial += valor;
      this.parcelas.push(this.novaParcela(index + 1, dataVencimento, valor));
    });
  }
}
